import { buildItemSize } from './settings';

const MIN_ITEM_WIDTH = 48;
const MAX_LENGTH = 1920;
const MAX_WEIGHT = 12;
const SNAP_RATIOS = [1 / 2, 1 / 3, 2 / 3, 1 / 4, 3 / 4];
const SNAP_DISTANCE = 0.02;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

function roundWeight(value) {
  return Math.max(0.01, Math.round(value * 100) / 100);
}

function snapRatio(ratio) {

  for (let index = 0; index < SNAP_RATIOS.length; index++) {
    if (Math.abs(ratio - SNAP_RATIOS[index]) <= SNAP_DISTANCE) {
      return SNAP_RATIOS[index];
    }
  }

  return ratio;

}

function formatPercent(ratio) {

  const left = Math.round(ratio * 100);

  return left + '% / ' + (100 - left) + '%';

}

// Both Fill: the weights keep their sum, unless that would pass the largest weight allowed.
function planFillPair(start, delta) {

  const pair = start.left.width + start.right.width;
  const ratio = snapRatio(clamp(start.left.width + delta, MIN_ITEM_WIDTH, pair - MIN_ITEM_WIDTH) / pair);
  const total = start.left.size.weight + start.right.size.weight;
  const scale = Math.min(1, MAX_WEIGHT / (total * Math.max(ratio, 1 - ratio)));

  return {
    left: buildItemSize('fill', roundWeight(total * ratio * scale)),
    right: buildItemSize('fill', roundWeight(total * (1 - ratio) * scale)),
    label: formatPercent(ratio),
    applied: null
  };

}

// A Fixed item keeps its unit: a percentage stays a percentage of the row.
function buildFixedSize(size, width, containerWidth) {

  if (size.mode === 'fixed' && /%$/.test(size.basis) === true && containerWidth > 0) {
    const percent = Math.round(width / containerWidth * 10000) / 100;
    return { changes: buildItemSize('fixed', percent + '%'), label: percent + '%' };
  }

  const pixels = Math.round(Math.min(width, MAX_LENGTH));

  return { changes: buildItemSize('fixed', pixels + 'px'), label: pixels + 'px' };

}

// One item Fill: the other becomes Fixed and the Fill item takes the rest. In a row sized to its
// content the Fill item has no rest to give, so the row grows instead, as with no Fill item.
function planBesideFill(start, delta) {

  const pair = start.left.width + start.right.width;

  if (start.left.size.mode === 'fill') {
    const rightWidth = clamp(start.right.width - delta, MIN_ITEM_WIDTH, pair - MIN_ITEM_WIDTH);
    const right = buildFixedSize(start.right.size, rightWidth, start.containerWidth);
    return { left: null, right: right.changes, label: right.label, applied: start.right.width - rightWidth };
  }

  const leftWidth = clamp(start.left.width + delta, MIN_ITEM_WIDTH, pair - MIN_ITEM_WIDTH);
  const left = buildFixedSize(start.left.size, leftWidth, start.containerWidth);

  return { left: left.changes, right: null, label: left.label, applied: leftWidth - start.left.width };

}

// No Fill item: the left one becomes or stays Fixed, and grows only into free space in the row.
// Free space can sit on both sides of the items, so the gap may move less than the item grows.
function planWithoutFill(start, delta) {

  const width = clamp(start.left.width + delta, MIN_ITEM_WIDTH, start.left.width + Math.max(start.free, 0));
  const fixed = buildFixedSize(start.left.size, width, start.containerWidth);

  return { left: fixed.changes, right: null, label: fixed.label, applied: width - start.left.width };

}

// start: { left: { size, width }, right: { size, width }, containerWidth, free }, with left and
// right as they show on screen and size from readGapItemSize. delta: px the handle moved right.
// applied is the delta used after limits, for the caller to land the gap under the pointer; it is
// null for two Fill items, whose gap always follows and whose snapping must not be corrected.
export function planGapResize(start, delta) {

  if (start.left.size.mode === 'fill' && start.right.size.mode === 'fill') {
    return planFillPair(start, delta);
  }

  if (start.left.size.mode === 'fill' || start.right.size.mode === 'fill') {
    return planBesideFill(start, delta);
  }

  return planWithoutFill(start, delta);

}
